import { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MoldConcentration } from '@ivario-arc/shared/src/enums.ts';
import { classNames } from '@utils/classNames';
import { ParameterDto } from '@src/types/types';
import { ParameterListItemLeft } from '@components/atoms/ParameterListItemLeft.tsx';
import {
  getMoldConcentrationFromRegulation,
  getNumericValue,
} from '@utils/parameters.ts';
import { ActiveLink } from '@components/atoms/ActiveLink.tsx';
import { ParameterDetailPopup } from '@components/atoms/ParameterDetailPopup.tsx';

function SedimentMoldParameterValue({
  parameter,
  concentration,
}: {
  parameter: ParameterDto;
  concentration: MoldConcentration;
}) {
  const { t } = useTranslation('component', { keyPrefix: 'parameterList' });
  let parameterColor = 'text-green';
  if (concentration === MoldConcentration.MEDIUM_MOLD) {
    parameterColor = 'text-orange';
  } else if (concentration === MoldConcentration.HIGH_MOLD) {
    parameterColor = 'text-error';
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-[140px_auto] w-full md:w-96">
      <div className="text-grey1 md:order-3">{t(`moldConcentration.name`)}</div>
      <div
        className={classNames(
          'font-bold md:order-1 text-end md:text-start',
          parameterColor,
        )}
      >
        {t(`moldConcentration.${concentration}`)}
      </div>
      <div className="text-grey1 md:order-4">{t(`CFU`)}</div>
      <div className="font-bold text-dark md:order-2  text-end md:text-start">
        {parameter.value}
      </div>
    </div>
  );
}

export const SedimentMoldParameter = ({
  parameter,
}: PropsWithChildren<{ parameter: ParameterDto }>) => {
  const { t } = useTranslation('component', { keyPrefix: 'parameterList' });
  const { name, category, regulations } = parameter;
  const [showDetail, setShowDetail] = useState(false);
  const numericValue = getNumericValue(parameter);
  let parameterConcentration = MoldConcentration.NO_MOLD;
  for (const regulation of regulations) {
    if (
      numericValue > 0 &&
      (!regulation.lowerLimit || numericValue >= regulation.lowerLimit) &&
      (!regulation.limit || numericValue < regulation.limit)
    ) {
      parameterConcentration = getMoldConcentrationFromRegulation(regulation);
    }
  }
  let statusBorderColor = '';
  if (parameterConcentration === MoldConcentration.MEDIUM_MOLD) {
    statusBorderColor = 'border-2 border-orange';
  } else if (parameterConcentration === MoldConcentration.HIGH_MOLD) {
    statusBorderColor = 'border-2 border-error';
  }

  return (
    <div
      className={classNames(
        'rounded-bonusLarge bg-grey3light print:break-inside-avoid',
        statusBorderColor,
      )}
      data-testid="sediment-mold-parameter"
    >
      <div
        className={classNames(
          'flex items-start md:items-center p-2.5 md:p-5 md:space-x-8 justify-between md:flex-row flex-col',
        )}
        data-testid="sediment-mold-parameter-header"
      >
        <ParameterListItemLeft
          subtitle={category}
          title={name ?? 'Unknown'}
          parameter={parameter}
          wide
        />
        <SedimentMoldParameterValue
          parameter={parameter}
          concentration={parameterConcentration}
        />
        <ActiveLink
          onTap={() => setShowDetail(true)}
          isActive={true}
          className="hidden md:block"
        >
          <span className="text-ivarioBlue">{t('moreInfoOnParam')}</span>
        </ActiveLink>
      </div>
      {showDetail && (
        <ParameterDetailPopup
          parameter={parameter}
          onClose={() => setShowDetail(false)}
        />
      )}
    </div>
  );
};
